import { useMemo, useState } from "react";
import { catalog } from "@/mocks/catalog";
import type { CatalogItem } from "@/pages/home/types";
import SectionHeading from "@/pages/home/components/SectionHeading";
import TitleCard from "@/pages/home/components/TitleCard";

const data = catalog as CatalogItem[];

const typeOptions = ["全部", "剧集", "电影", "动漫", "综艺", "纪录片"];
const qualityOptions = ["全部", "蓝光4K", "1080P", "HD高清"];

interface LibraryProps {
  query: string;
  onQueryChange: (value: string) => void;
}

export default function LibrarySection({ query, onQueryChange }: LibraryProps) {
  const [type, setType] = useState("全部");
  const [quality, setQuality] = useState("全部");
  const [tag, setTag] = useState("");

  const tagOptions = useMemo(() => {
    const set = new Set<string>();
    data.forEach((it) => it.tags.forEach((t) => set.add(t)));
    return Array.from(set).slice(0, 8);
  }, []);

  const list = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return data.filter((it) => {
      const matchKw =
        !kw ||
        it.title.toLowerCase().includes(kw) ||
        it.type.toLowerCase().includes(kw) ||
        it.region.toLowerCase().includes(kw) ||
        it.quality.toLowerCase().includes(kw) ||
        it.episodes.toLowerCase().includes(kw) ||
        it.genres.some((g) => g.toLowerCase().includes(kw)) ||
        it.tags.some((t) => t.toLowerCase().includes(kw));
      const matchType = type === "全部" || it.type === type;
      const matchQuality = quality === "全部" || it.quality === quality;
      const matchTag = !tag || it.tags.includes(tag);
      return matchKw && matchType && matchQuality && matchTag;
    });
  }, [query, type, quality, tag]);

  const hasFilter = query.trim() !== "" || type !== "全部" || quality !== "全部" || tag !== "";

  const resetAll = () => {
    onQueryChange("");
    setType("全部");
    setQuality("全部");
    setTag("");
  };

  return (
    <section id="library" className="scroll-mt-20 bg-background-50">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="全网资源库"
          title="按关键词、类型与画质，一点点筛出你要的那部"
          desc="这是本页的检索区，所有搜索与筛选都只作用于页面上已收录的条目，切换条件时结果会即时更新，不需要刷新页面。"
        />

        <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-4 md:p-5">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300/60 bg-background-50 px-3">
                <i className="ri-search-line text-sm text-foreground-500"></i>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => onQueryChange(e.target.value)}
                  placeholder="搜索片名、类型、地区或标签"
                  aria-label="在资源库中搜索"
                  className="h-11 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
                {query && (
                  <button
                    type="button"
                    aria-label="清空搜索"
                    onClick={() => onQueryChange("")}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-200 cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
              {hasFilter && (
                <button
                  type="button"
                  onClick={resetAll}
                  className="inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 bg-background-50 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">类型</span>
                {typeOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    aria-pressed={type === opt}
                    onClick={() => setType(opt)}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors cursor-pointer ${
                      type === opt
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300/60 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">画质</span>
                {qualityOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    aria-pressed={quality === opt}
                    onClick={() => setQuality(opt)}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors cursor-pointer ${
                      quality === opt
                        ? "bg-accent-500 text-background-50"
                        : "border border-background-300/60 bg-background-50 text-foreground-600 hover:border-accent-300 hover:text-accent-700"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-background-200/70 pt-3">
              <span className="text-xs text-foreground-500">标签</span>
              <button
                type="button"
                onClick={() => setTag("")}
                aria-pressed={tag === ""}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors cursor-pointer ${
                  tag === ""
                    ? "bg-secondary-500 text-background-50"
                    : "border border-background-300/60 bg-background-50 text-foreground-600 hover:border-secondary-300"
                }`}
              >
                不限
              </button>
              {tagOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setTag(opt)}
                  aria-pressed={tag === opt}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors cursor-pointer ${
                    tag === opt
                      ? "bg-secondary-500 text-background-50"
                      : "border border-background-300/60 bg-background-50 text-foreground-600 hover:border-secondary-300"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            共匹配到 <span className="font-semibold text-primary-700">{list.length}</span> 条结果
            {query.trim() && <span className="text-foreground-500"> · 关键词「{query.trim()}」</span>}
          </p>
        </div>

        {list.length > 0 ? (
          <div className="mt-5 grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {list.map((item) => (
              <TitleCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-14 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-400"></i>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有找到匹配的条目</h3>
            <p className="mt-2 text-sm text-foreground-600">
              试试更换关键词，或放宽类型与画质条件。当前检索范围仅限本页已收录内容。
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              清空全部条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}